import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

/**
 * The duplicate-record training transfer: when a person's passes landed on
 * an auto-provisioned learner record, HR copies them onto the real record.
 * Server appends staff-attributed completion events; nothing on the source
 * record changes.
 */

export interface TransferSourcePerson {
  id: string;
  full_name: string;
  email: string;
}

export interface TransferCandidate {
  task_id: string;
  training_module_id: string;
  title: string;
  source: { attempt_id: string; score: number; completed_at: string } | null;
}

export interface TransferPreview {
  source_person: { id: string; full_name: string };
  candidates: TransferCandidate[];
}

export interface TransferOutcome {
  transferred: { task_id: string; title: string; score: number }[];
  skipped: { task_id: string; title: string; reason: string }[];
}

/** Candidate source records, including hidden training-learner people. */
export function useTransferSources(enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'training-transfer-sources'],
    enabled,
    queryFn: async () =>
      (await api<{ people: TransferSourcePerson[] }>('/api/admin/training-progress/transfer-sources'))
        .people,
  });
}

export function useTransferPreview(personId: string) {
  return useMutation({
    mutationFn: (source_person_id: string) =>
      api<TransferPreview>(`/api/admin/training-progress/${personId}/transfer-preview`, {
        method: 'POST',
        body: JSON.stringify({ source_person_id }),
      }),
  });
}

export function useTransferTrainingPasses(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { source_person_id: string; task_ids: string[]; reason: string }) =>
      api<TransferOutcome>(`/api/admin/training-progress/${personId}/transfer`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['person-training-modules', personId] });
      qc.invalidateQueries({ queryKey: ['person-onboarding', personId] });
      qc.invalidateQueries({ queryKey: ['admin', 'training-progress'] });
    },
  });
}
